import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Alert,
  Row,
  Col,
  Typography,
  Space,
  Progress,
  message,
  Divider,
  Statistic,
  Avatar,
} from "antd";
import {
  LockOutlined,
  KeyOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import zxcvbn from "zxcvbn";
import axios from "axios";
import config from "../../../config";

const { Title, Text } = Typography;

const UpdatePassword = () => {
  const account = useSelector((state) => state.account);
  const user = account?.user;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordScore, setPasswordScore] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const history = useHistory();
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    if (value) {
      const result = zxcvbn(value);
      setPasswordStrength(result.score);
      setPasswordScore(result.score);
    } else {
      setPasswordStrength(0);
      setPasswordScore(0);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
        id: user.id,
        email: user.email,
      };
      const { data } = await api.post(`/user-password-change`, payload);
      message.success(data.message || "Password updated successfully");
      form.resetFields();
      setSuccess(data.message);
      setTimeout(() => {
        history.push("/dashboard");
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to update password";
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "#ff4d4f";
      case 2:
        return "#faad14";
      case 3:
      case 4:
        return "#52c41a";
      default:
        return "#d9d9d9";
    }
  };

  const getStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        return "Strong";
      default:
        return "Not entered";
    }
  };

  const getStrengthPercent = () => {
    return (passwordStrength + 1) * 20;
  };

  const passwordRequirements = [
    { text: "At least 8 characters", met: (pwd) => pwd?.length >= 8 },
    { text: "Contains uppercase letter", met: (pwd) => /[A-Z]/.test(pwd) },
    { text: "Contains lowercase letter", met: (pwd) => /[a-z]/.test(pwd) },
    { text: "Contains number", met: (pwd) => /[0-9]/.test(pwd) },
    { text: "Contains special character", met: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
  ];

  const newPassword = Form.useWatch("newPassword", form);

  return (
    <>
      <Row gutter={[24, 24]} justify="space-between">
        <Col xs={24} sm={20} md={16} lg={12}>
        <Card
            title={
              <Space>
                <LockOutlined style={{ color: "#1677ff" }} />
                <span>Update Password</span>
              </Space>
            }
            bordered={false}
            size="small"
            style={{ marginTop: 16}}
          >
            {/* User Info Section */}
            <div
              style={{
                textAlign: "center",
                marginBottom: 24,
                padding: 16,
                backgroundColor: "#f5f5f5",
                borderRadius: 12,
              }}
            >
              <Avatar
                size={64}
                style={{ backgroundColor: "#1677ff", marginBottom: 12 }}
                icon={<SafetyOutlined />}
              />
              <Title level={4}>{user?.firstname} {user?.lastname}</Title>
              <Text type="secondary">User ID: {user?.id}</Text>
              <br />
              <Text type="secondary">Email: {user?.email}</Text>
            </div>

            {success && (
              <Alert
                message="Success"
                description={success}
                type="success"
                showIcon
                closable
                style={{ marginBottom: 16 }}
                onClose={() => setSuccess(null)}
              />
            )}

            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                closable
                style={{ marginBottom: 16 }}
                onClose={() => setError(null)}
              />
            )}

            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: "Please enter your current password" }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter current password"
                  size="large"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Divider />

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: "Please enter a new password" },
                  { min: 8, message: "Password must be at least 8 characters" },
                ]}
              >
                <Input.Password
                  prefix={<KeyOutlined />}
                  placeholder="Enter new password"
                  size="large"
                  onChange={handlePasswordChange}
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              {newPassword && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text>Password Strength</Text>
                      <Text style={{ color: getStrengthColor(), fontWeight: "bold" }}>
                        {getStrengthText()}
                      </Text>
                    </div>
                    <Progress
                      percent={getStrengthPercent()}
                      strokeColor={getStrengthColor()}
                      showInfo={false}
                      size="small"
                    />
                    <div style={{ marginTop: 12 }}>
                      <Text strong style={{ fontSize: 12 }}>
                        Password Requirements:
                      </Text>
                      <div style={{ marginTop: 8 }}>
                        {passwordRequirements.map((req, idx) => (
                          <div key={idx} style={{ marginBottom: 4 }}>
                            {req.met(newPassword) ? (
                              <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                            ) : (
                              <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                            )}
                            <Text type={req.met(newPassword) ? "success" : "secondary"} style={{ fontSize: 12 }}>
                              {req.text}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: "Please confirm your new password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match!"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirm new password"
                  size="large"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Form.Item>
                <Space style={{ width: "100%", justifyContent: "center" }}>
                  <Button type="primary" htmlType="submit" loading={loading} size="large">
                    Update Password
                  </Button>
                  <Button size="large" onClick={() => history.goBack()}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
          </Col>
        <Col xs={24} sm={20} md={16} lg={12}>
          <Card
            size="small"
            style={{ marginTop: 16 }}
          >
            <Space direction="vertical">
              <Text strong>
                <SafetyOutlined style={{ marginRight: 8 }} />
                Security Tips
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                • Never share your password with anyone
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                • Use a unique password for this account
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                • Enable two-factor authentication for added security
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default UpdatePassword;